import React, { useCallback } from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { Button, ButtonProps, Modal } from "@mapmycustomers/ui";

import { getLegacyAppUrl } from "@app/util/appUrl";

import configService from "../../config/ConfigService";
import authService from "../../store/auth/AuthService";
import VimeoVideo from "../VimeoVideo";

import styles from "./BaseInfoModal.module.scss";
import BaseModalProps from "./BaseModalProps";

export interface VideoInfoModalProps extends BaseModalProps {
  actionButtonProps?: ButtonProps;
  actionButtonTitle?: string;
  actionLegacyLink?: string;
  actionLink?: string;
  moreInfoButtonTitle?: string;
  onAction?: () => void;
  onClickVideo?: () => void;
  onHide: () => void;
  onMoreResource?: () => void;
  resourceLink?: string;
  title: string;
  videoId?: string;
  visible: boolean;
}

const VideoInfoModal: React.FC<VideoInfoModalProps> = ({
  actionButtonProps,
  actionButtonTitle,
  actionLegacyLink,
  actionLink,
  children,
  moreInfoButtonTitle,
  onAction,
  onClickVideo,
  onHide,
  onMoreResource,
  resourceLink,
  title,
  videoId,
  visible,
}) => {
  const intl = useIntl();
  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();

  const legacyUrl =
    actionLegacyLink && token ? getLegacyAppUrl(baseOldAppUrl, token, actionLegacyLink) : null;

  const handleEmptyAction = useCallback(() => {
    if (onAction) {
      onAction();
    }
    onHide();
  }, [onAction, onHide]);

  return (
    <Modal
      destroyOnClose
      footer={null}
      onCancel={onHide}
      title={title}
      visible={visible}
      width={600}
    >
      {videoId && (
        <VimeoVideo
          onClick={onClickVideo}
          title={intl.formatMessage({
            id: "onboarding.baseInfoModal.video",
            defaultMessage: "Video Player",
            description: "Title for onboarding base modal video",
          })}
          videoClassName={styles.videoContainer}
          videoId={videoId}
        />
      )}
      <div className={styles.videoDescription}>{children}</div>
      <section className={styles.actions}>
        <Button
          disabled={!resourceLink}
          href={resourceLink}
          onClick={onMoreResource}
          rel="noreferrer"
          target="_blank"
          type="default"
        >
          {moreInfoButtonTitle ? (
            moreInfoButtonTitle
          ) : (
            <FormattedMessage
              id="onboarding.baseInfoModal.button.moreResources"
              defaultMessage="More Resources"
              description="Onboarding base modal More Resources button"
            />
          )}
        </Button>
        {actionLink && (
          <Link target="_blank" to={actionLink}>
            <Button onClick={onAction} type="primary" {...actionButtonProps}>
              {actionButtonTitle}
            </Button>
          </Link>
        )}
        {legacyUrl && (
          <a href={legacyUrl} rel="noreferrer" target="_blank">
            <Button onClick={onAction} type="primary" {...actionButtonProps}>
              {actionButtonTitle}
            </Button>
          </a>
        )}
        {!actionLink && !legacyUrl && actionButtonTitle && (
          // otherwise it works like a "Close" button
          <Button key="submit" onClick={handleEmptyAction} type="primary">
            {actionButtonTitle}
          </Button>
        )}
      </section>
    </Modal>
  );
};

export default VideoInfoModal;
